import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ArrowLeft, Info } from 'lucide-react';

import ReleaseConfirmDialog from '@/components/release/ReleaseConfirmDialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QualityDetailContext } from '@/contexts/QualityContext';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { useQuality } from '@/hooks/useQualitiesAPI';
import PageLayout from '@/layouts/PageLayout';
import { apiService } from '@/lib/axios';
import QualityApprovalsView from '@/views/qualities/QualityApprovalsView';
import QualityInformationView from '@/views/qualities/QualityInformationView';
import QualityNonconformityView from '@/views/qualities/QualityNonconformityView';
import QualityPlanView from '@/views/qualities/QualityPlanView';

const getReleaseTarget = (quality) =>
  quality?.planChecked ? 'Pending Action' : 'Closed';

const QualityReleasePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();
  const { quality, loading } = useQuality(id);
  const [activeTab, setActiveTab] = useState('information');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const canRelease = quality?.status === 'APPROVED';

  const setFormByKey = () => {};

  const handleRelease = async () => {
    if (!quality || !canRelease) return;

    setReleasing(true);
    try {
      const res = await apiService.qualities.release(quality.id);

      if (res.data.success) {
        showNotificationModal(
          'Release Successful',
          'The quality event has been released and moved to the Master List.',
          'success'
        );
        navigate('/qualities', { state: 'master' });
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Failed to release quality event';
      showNotificationModal('Release Failed', message, 'error');
    } finally {
      setReleasing(false);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <QualityDetailContext.Provider
      value={{
        activeTab,
        form: quality || {},
        id,
        mode: 'static',
        reviews: [],
        setActiveTab,
        setFormByKey,
      }}
    >
      <PageLayout
        title={`Release Quality Event: ${quality?.serial || quality?.id || id}`}
        toolbarHead={
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        }
        toolbarTail={
          <Button
            disabled={!canRelease || releasing}
            onClick={() => setConfirmDialogOpen(true)}
          >
            Release
          </Button>
        }
      >
        {quality && (
          <div className="flex items-center gap-2 mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
            <Info className="h-4 w-4 text-blue-500 shrink-0" />
            <span>
              <span className="font-medium">Releasing:</span>{' '}
              {quality.serial || quality.id} -{' '}
              {quality.informationDescription || 'Quality Event'} to{' '}
              {getReleaseTarget(quality)}
            </span>
          </div>
        )}

        {!loading && quality && !canRelease && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Only approved quality events can be released.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Loading quality event...
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap">
              <TabsTrigger value="information">
                Quality Event Information
              </TabsTrigger>
              <TabsTrigger value="nonconformity">
                Nonconformity Identification
              </TabsTrigger>
              <TabsTrigger value="plan">Action Plan</TabsTrigger>
              <TabsTrigger value="approvals">Approvals</TabsTrigger>
            </TabsList>

            <TabsContent value="information" className="mt-0 space-y-8">
              <QualityInformationView />
            </TabsContent>
            <TabsContent value="nonconformity" className="mt-0">
              <QualityNonconformityView />
            </TabsContent>
            <TabsContent value="plan" className="mt-0">
              <QualityPlanView />
            </TabsContent>
            <TabsContent value="approvals" className="mt-0 space-y-8">
              <QualityApprovalsView />
            </TabsContent>
          </Tabs>
        )}

        <ReleaseConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleRelease}
          title="Confirm Quality Event Release"
          description="You are about to release this quality event."
          details={[
            {
              label: 'Quality Event',
              value: quality?.serial || quality?.id || id,
            },
            {
              label: 'Requestor',
              value: quality?.creatorName || quality?.creator || '',
            },
            {
              label: 'Action Plan',
              value: quality?.planChecked ? 'Required' : 'Not required',
            },
            {
              label: 'Next Status',
              value: getReleaseTarget(quality),
            },
          ]}
          loading={releasing}
        />
      </PageLayout>
    </QualityDetailContext.Provider>
  );
};

export default QualityReleasePage;
